'use client'

import { isServer, MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { UserContextProvider, useUserContext } from '@/lib/contexts/UserContext'
import { EventEmitterProvider, useEventEmitterContext } from '@/lib/contexts/EventEmitterContext'
import { getBugsnagClient } from '@/lib/bugsnag/bugsnag'
import { toast } from 'sonner'
import { Toaster } from '@/components/ui/sonner'
import { UserRealtimeContextProvider } from '@/lib/contexts/UserRealtimeContext'
import OneSignal, { SubscriptionChangeEvent } from 'react-onesignal'
import { features, ONESIGNAL_APP_ID } from '@/lib/config'
import { type PushSubscription, registerPushSubscription, unregisterPushSubscription } from '@/lib/crate-core'
import { AttributionTracking } from '@/components/attribution-tracking'
import AudioPlayer from '@/components/audio/AudioPlayer'
import { CrateCartAssetOpenListener } from '@/components/marketplace/crate-cart-asset-open-listener'
import { HeaderFeaturesProvider } from '@/lib/contexts/HeaderFeaturesContext'
import { DownloadRefusalDialogs } from '@/components/download-protection/download-refusal-dialogs'

function onQueryClientError(error: Error) {
  console.error(error)
  getBugsnagClient()?.notify(error)
  toast.error('An error occurred. Please try again later.')
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
        // Don't refetch on window focus
        refetchOnWindowFocus: false,
        // Do not retry on error
        retry: false,
      },
      mutations: {
        // Do not retry on error
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: onQueryClientError,
    }),
    mutationCache: new MutationCache({
      onError: onQueryClientError,
    }),
  })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient()
    return browserQueryClient
  }
}

// This is a bit of a hack, but we need to start Bugsnag
function startBugsnag() {
  getBugsnagClient()
}

/**
 * Asset download analytics listener.
 */
function AssetDownloadAnalytics() {
  const appEvent$ = useEventEmitterContext()

  appEvent$.useSubscription((evt) => {
    if (evt.type !== 'asset:download') {
      return
    }

    if (window.getGlobalSession && typeof window.getGlobalSession === 'function') {
      const session = window.getGlobalSession()

      if (
        session &&
        session.analytics &&
        session.analytics.sendCustomEvent &&
        typeof session.analytics.sendCustomEvent === 'function'
      ) {
        console.debug('SENDING CUSTOM EVENT', {
          type: evt.payload?.type,
          location: evt.payload?.location,
          tags: evt.payload?.tags,
          downloadParams: evt.payload?.downloadParams,
        })
        session.analytics.sendCustomEvent('asset_download', {
          type: evt.payload?.type,
          location: evt.payload?.location,
          tags: evt.payload?.tags,
          downloadParams: evt.payload?.downloadParams,
        })
      }
    }
  })

  return null
}

/**
 * OneSignal setup component.
 * @constructor
 */
function OneSignalSetup() {
  const [initialized, setInitialized] = useState(false)
  const { user } = useUserContext()
  const isEnabled = features.pushNotifications && Boolean(ONESIGNAL_APP_ID)

  // Initialize OneSignal on mount
  useEffect(() => {
    if (!isEnabled) return
    setInitialized(false)

    console.debug('Initializing OneSignal...')

    OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      autoRegister: true,
      autoResubscribe: false,
      allowLocalhostAsSecureOrigin: true,
    })
      .then(() => {
        console.debug('OneSignal initialized')
        setInitialized(true)
      })
      .catch((err) => {
        console.error('OneSignal initialization failed', err)
      })
  }, [isEnabled])

  // When the user changes, log in to OneSignal
  useEffect(() => {
    if (!initialized || !isEnabled) return

    if (user && user.id) {
      console.debug('Logging in to OneSignal for user', user.id)
      OneSignal.login(user.id.toString())
        .then(() => {
          console.debug('OneSignal logged in for user', user.id)

          if (typeof window !== 'undefined' && window.OneSignalDeferred) {
            window.OneSignalDeferred.push(async (OneSignal) => {
              try {
                const subscription = OneSignal.User.PushSubscription
                if (subscription?.optedIn && subscription?.id && subscription?.token) {
                  const pushSubscription: PushSubscription = {
                    onesignal_id: subscription.id,
                    token: subscription.token,
                    platform: 'web',
                  }
                  await registerPushSubscription(pushSubscription)
                  console.debug('Push subscription synced on OneSignal login')
                }
              } catch (err) {
                console.error('Failed to sync push subscription on OneSignal login:', err)
              }
            })
          }
        })
        .catch((err) => {
          console.error('OneSignal login failed', err)
        })
    } else {
      console.debug('Logging out of OneSignal')
      OneSignal.logout()
        .then(() => {
          console.debug('OneSignal logged out')
        })
        .catch((err) => {
          console.error('OneSignal logout failed', err)
        })
    }
  }, [initialized, isEnabled, user])

  // Login on subscription change
  useEffect(() => {
    if (!initialized || !isEnabled) return

    const handleSubscriptionChange = async (event: SubscriptionChangeEvent) => {
      const previous = event.previous
      const current = event.current

      const wasSubscribed = previous?.optedIn ?? false
      const isSubscribed = current?.optedIn ?? false

      const tokenChanged = wasSubscribed && isSubscribed && previous?.token !== current?.token

      try {
        if (tokenChanged) {
          if (current?.id && current?.token) {
            const subscription: PushSubscription = {
              onesignal_id: current.id,
              token: current.token,
              platform: 'web',
            }
            await registerPushSubscription(subscription)
            console.debug('Push token updated and synced with backend')
          }
        } else if (!wasSubscribed && isSubscribed && current?.id && current?.token) {
          const subscription: PushSubscription = {
            onesignal_id: current.id,
            token: current.token,
            platform: 'web',
          }
          await registerPushSubscription(subscription)
          console.debug('Push subscription registered with backend')
        } else if (wasSubscribed && !isSubscribed && previous?.token) {
          await unregisterPushSubscription(previous.token)
          console.debug('Push subscription unregistered from backend')
        }

        if (user && user.id) {
          await OneSignal.login(user.id.toString())
          console.debug('OneSignal re-logged in for user', user.id)
        }
      } catch (err) {
        console.error('Failed to sync push subscription with backend:', err)
      }
    }

    if (typeof window !== 'undefined') {
      window.OneSignalDeferred = window.OneSignalDeferred || []

      window.OneSignalDeferred.push(async function (OneSignal) {
        OneSignal.User.PushSubscription.addEventListener('change', handleSubscriptionChange)
      })
    }

    return () => {
      if (typeof window !== 'undefined' && window.OneSignal) {
        window.OneSignal.User.PushSubscription.removeEventListener('change', handleSubscriptionChange)
      }
    }
  }, [initialized, isEnabled, user])

  return null
}

export default function Providers({ children }: { children: React.ReactNode }) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient()

  // Start Bugsnag on mount
  useEffect(() => {
    startBugsnag()
  }, [])

  return (
    <>
      <HeaderFeaturesProvider>
        <UserContextProvider>
          <UserRealtimeContextProvider>
            <QueryClientProvider client={queryClient}>
              <EventEmitterProvider>
                <AudioPlayer />
                {children}
                <AssetDownloadAnalytics />
                <CrateCartAssetOpenListener />
                <AttributionTracking />
                {features.pushNotifications ? <OneSignalSetup /> : null}
              </EventEmitterProvider>
              <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
            </QueryClientProvider>
          </UserRealtimeContextProvider>
        </UserContextProvider>
      </HeaderFeaturesProvider>
      <Toaster className="tw-pointer-events-auto" richColors position="bottom-center" />
      <DownloadRefusalDialogs />
    </>
  )
}
